<template>
  <v-container fluid>
    <h1>{{ $t('license.licenseManagement') }}</h1>

    <!-- Toolbar with toggle for deleted items -->
    <v-toolbar flat class="mb-2">
      <v-toolbar-title>{{ $t('license.licenses') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        :color="showDeleted ? 'error' : 'grey'"
        variant="outlined"
        class="mr-4"
        @click="showDeleted = !showDeleted"
      >
        <v-icon start>{{ showDeleted ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
        {{ showDeleted ? $t('license.hideDeleted') : $t('license.showDeleted') }}
      </v-btn>
      <add-license @license-added="fetchLicenses" />
    </v-toolbar>

    <v-data-table
      :headers="computedHeaders"
      :items="filteredLicenses"
      :loading="loading"
      v-model:options="options"
      :items-per-page="50"
      class="elevation-1"
      fixed-header
      height="calc(100vh - 300px)"
      :row-props="getRowProps"
    >
      <template v-slot:top>
        <v-toolbar flat density="compact" class="px-4">
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <span class="mr-4">{{ $t('license.licenses') }}: {{ filteredLicenses.length }}</span>
          </div>
        </v-toolbar>
      </template>

      <template v-slot:[`item.online_status`]="{ item }">
        <v-icon :color="getOnlineStatusColor(item.last_online_time)">
          mdi-circle
        </v-icon>
      </template>

      <template v-slot:[`item.expire_date_display`]="{ item }">
        <strong>{{ item.valid_date_display }} ({{ formatExpireDateRelative(item.valid_date) }})</strong>
      </template>

      <template v-slot:[`item.last_online_time_display`]="{ item }">
        {{ formatRelativeTimeWithSuffix(item.last_online_time) }}
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex ga-2 justify-center">
          <edit-license :license="item" @license-updated="fetchLicenses" :disabled="item.is_deleted === 1" />
          <v-btn
            v-if="item.is_deleted === 0"
            icon
            color="error"
            size="small"
            @click="confirmDeleteLicense(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-btn
            v-if="item.is_deleted === 1"
            icon
            color="success"
            size="small"
            @click="confirmRecoverLicense(item)"
          >
            <v-icon>mdi-restore</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">
          {{ $t('license.confirmDelete') }}
        </v-card-title>
        <v-card-text>
          {{ $t('license.deleteWarning', { machineId: selectedLicense?.machine_id }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="deleteDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="error" variant="text" @click="deleteLicense">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Recover Confirmation Dialog -->
    <v-dialog v-model="recoverDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h5">
          {{ $t('license.confirmRecover') }}
        </v-card-title>
        <v-card-text>
          {{ $t('license.recoverWarning', { machineId: selectedLicense?.machine_id }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="recoverDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="success" variant="text" @click="recoverLicense">
            {{ $t('common.recover') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import {ref, onMounted, computed, onUnmounted} from 'vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import AddLicense from '@/components/AddLicense.vue'
import EditLicense from '@/components/EditLicense.vue'

const { t } = useI18n()
const licenses = ref([])
const loading = ref(true)
const options = ref({
  page: 1,
  itemsPerPage: 50
})
const fetchInterval = ref(null)
const deleteDialog = ref(false)
const recoverDialog = ref(false)
const selectedLicense = ref(null)
const showDeleted = ref(false)

const computedHeaders = computed(() => [
  {title: t('license.status'), key: 'online_status', sortable: false, width: '80px'},
  {title: t('license.index'), key: 'id', width: '80px'},
  {title: t('license.serialNumber'), key: 'machine_id', width: '200px'},
  {title: t('license.clientName'), key: 'client_name', width: '150px'},
  {title: t('license.expireDate'), key: 'expire_date_display', width: '300px', class: 'font-weight-bold'},
  {title: t('license.lastOnline'), key: 'last_online_time_display', width: '250px'},
  {title: t('license.actions'), key: 'actions', sortable: false, width: '120px'}
])

const filteredLicenses = computed(() => {
  if (showDeleted.value) {
    return licenses.value
  }
  return licenses.value.filter(license => license.is_deleted === 0)
})

onMounted(() => {
  fetchLicenses()
  fetchInterval.value = setInterval(fetchLicenses, 5000)
})

onUnmounted(() => {
  if (fetchInterval.value) {
    clearInterval(fetchInterval.value)
  }
})

function getOnlineStatusColor(lastOnlineTime) {
  const currentTime = Math.floor(Date.now())
  const timeDifference = currentTime - lastOnlineTime
  return timeDifference < 180*1000 ? 'green' : 'red'
}

function isOnline(lastOnlineTime) {
  const currentTime = Math.floor(Date.now())
  const timeDifference = currentTime - lastOnlineTime
  return timeDifference < 180*1000
}

function getRowProps({ item }) {
  return {
    style: isOnline(item.last_online_time) ? 'background-color: #e8f5e9;' : ''
  }
}

function parseDate(dateString) {
  const [datePart, timePart] = dateString.split('-')
  const year = parseInt(datePart.substring(0, 4))
  const month = parseInt(datePart.substring(4, 6)) - 1
  const day = parseInt(datePart.substring(6, 8))
  const hours = parseInt(timePart.substring(0, 2))
  const minutes = parseInt(timePart.substring(2, 4))
  const seconds = parseInt(timePart.substring(4, 6))
  return new Date(year, month, day, hours, minutes, seconds)
}

function formatDate(dateString) {
  return parseDate(dateString).toLocaleDateString('en-CA')
}

function formatRelativeTime(timestamp) {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffYears = Math.floor(diffDays / 365)

  // More than 1 year: show years only
  if (diffDays >= 365) {
    return { value: diffYears, unit: 'years' }
  }

  // Less than 1 year but more than 1 day: show months only
  if (diffDays > 1) {
    const months = Math.floor(diffDays / 30)
    return { value: months, unit: 'months' }
  }

  // Less than 1 day: show hours
  if (diffHours > 0) {
    return { value: diffHours, unit: 'hours' }
  }

  // Less than 1 hour: show minutes
  if (diffMinutes > 0) {
    return { value: diffMinutes, unit: 'minutes' }
  }

  return { value: diffSeconds, unit: 'seconds' }
}

function formatRelativeTimeWithSuffix(timestamp) {
  const { value, unit } = formatRelativeTime(timestamp)
  return t(`time.${unit}Ago`, { count: value })
}

function formatExpireDateRelative(dateString) {
  const expireDate = parseDate(dateString).getTime()
  const now = Date.now()
  const diffMs = expireDate - now
  const isExpired = diffMs < 0

  const absDiffMs = Math.abs(diffMs)
  const diffDays = Math.floor(absDiffMs / (1000 * 60 * 60 * 24))
  const diffYears = Math.floor(diffDays / 365)

  let value, unit

  // More than 1 year
  if (diffDays >= 365) {
    value = diffYears
    unit = 'years'
  }
  // Less than 1 year but more than 1 day: show months
  else if (diffDays > 1) {
    value = Math.floor(diffDays / 30)
    unit = 'months'
  }
  // Less than 1 day: show days
  else {
    value = diffDays
    unit = 'days'
  }

  const suffix = isExpired ? 'Ago' : 'Later'
  return t(`time.${unit}${suffix}`, { count: value })
}

async function fetchLicenses() {
  loading.value = true
  try {
    const response = await axios.get('/api/licenses', {
      params: {
        skip: 0,
        limit: 100  // Max allowed by API
      }
    })
    licenses.value = response.data.licenses.map(license => ({
      ...license,
      valid_date_display: formatDate(license.valid_date)
    }))
  } catch (error) {
    console.error('Error fetching licenses:', error)
  } finally {
    loading.value = false
  }
}

function confirmDeleteLicense(item) {
  selectedLicense.value = item
  deleteDialog.value = true
}

function confirmRecoverLicense(item) {
  selectedLicense.value = item
  recoverDialog.value = true
}

async function deleteLicense() {
  try {
    await axios.delete(`/api/licenses/${selectedLicense.value.machine_id}`)
    await fetchLicenses()
    deleteDialog.value = false
    selectedLicense.value = null
  } catch (error) {
    console.error('Error deleting license:', error)
  }
}

async function recoverLicense() {
  try {
    await axios.post(`/api/licenses/${selectedLicense.value.machine_id}/recover`)
    await fetchLicenses()
    recoverDialog.value = false
    selectedLicense.value = null
  } catch (error) {
    console.error('Error recovering license:', error)
  }
}
</script>

<style scoped>
:deep(.v-data-table) {
  font-size: 14px;
}

:deep(.font-weight-bold) {
  font-weight: bold !important;
}

:deep(.v-data-table__th) {
  font-weight: bold !important;
  background-color: #f5f5f5 !important;
}
</style>
